import React from 'react';

import {getApolloClient} from 'graphql-client/ApolloClient';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import notifications from 'notifications';

/**
 * Clears dependent state when the modal owner changes. Skips the first render so edit flows can
 * keep their initial review-process selection until the user actually changes owner.
 *
 * @package
 */
export function useResetOnOwnerChange(ownerId: string, onReset: () => void): void {
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onReset();
  }, [ownerId, onReset]);
}

export const REVIEW_PROCESS_SAVE_ERROR_MESSAGE =
  "Sorry, the review process couldn't be updated, but your other changes saved. Reopen settings to try again.";

/**
 * The review process is saved via a REST/Celery endpoint, so Apollo can't observe that the
 * collection's reviewLifecycles changed. Evict the field so the collection page and a re-opened
 * settings modal refetch the fresh assignment instead of rendering stale cached data.
 *
 * @package
 */
export function evictReviewLifecyclesFromCache(collectionId: string): void {
  const {cache} = getApolloClient();
  cache.evict({
    id: cache.identify({__typename: 'TemplateCollectionNode', id: collectionId}),
    fieldName: 'reviewLifecycles',
  });
  cache.gc();
}

/**
 * Saves the collection's review process assignment and refreshes the Apollo cache. The main
 * collection create/update has already succeeded by the time this runs, so a failure here is
 * swallowed after notifying the user, letting the overall save flow complete. Pass `successMessage`
 * to surface a success toast (used by flows that save the review process fire-and-forget, so the
 * outcome isn't otherwise visible).
 *
 * @package
 */
export function persistReviewLifecycle(
  collectionId: string,
  reviewLifecycleIds: Set<string>,
  {successMessage}: {successMessage?: string} = {}
): Promise<void> {
  return TemplateCollectionDAO.setReviewLifecycle(collectionId, [...reviewLifecycleIds])
    .then(() => {
      evictReviewLifecyclesFromCache(collectionId);
      if (successMessage != null) {
        notifications.success(successMessage);
      }
    })
    .catch(() => {
      notifications.error(REVIEW_PROCESS_SAVE_ERROR_MESSAGE);
    });
}
